import React, { useEffect, useRef, useState } from 'react';
import { Button, Toast } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { PinGrayIcon, LocationTargetIcon } from '../../../../assets/icons';
import styles from '../../delivering/components/MapboxMapView.module.css';
import geolocationService from '../../services/GeolocationService';
import DriverLocationSvg from '../../../../assets/icons/svgs/driver-location.svg';

const DEFAULT_MAPBOX_API_KEY = "pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ";

interface AddressCoordinateFeedbackMapViewProps {
    latitude: number;
    longitude: number;
    originalLatitude?: number | null;
    originalLongitude?: number | null;
    onLocationSelect?: () => void;
    onCoordinateChange?: (latitude: number, longitude: number) => void;
    onCenterLabelChange?: (label: string) => void;
    showCenterIndicator?: boolean;
    enableDragToSelect?: boolean;
    zoom?: number;
    stopNumber?: string | number | null;
}

const AddressCoordinateFeedbackMapView: React.FC<AddressCoordinateFeedbackMapViewProps> = ({
    latitude,
    longitude,
    originalLatitude,
    originalLongitude,
    onLocationSelect,
    onCoordinateChange,
    onCenterLabelChange,
    showCenterIndicator = false,
    enableDragToSelect = false,
    zoom = 15,
    stopNumber = null
}) => {
    const { t } = useTranslation();
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const originalMarkerRef = useRef<any>(null);
    const currentLocationMarkerRef = useRef<any>(null);
    const currentLocationWatchIdRef = useRef<number | null>(null);
    const locationErrorShownRef = useRef<boolean>(false);
    const moveendHandlerRef = useRef<(() => void) | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [currentLatitude, setCurrentLatitude] = useState<number>(latitude);
    const [currentLongitude, setCurrentLongitude] = useState<number>(longitude);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [driverLocation, setDriverLocation] = useState<{ lat: number; lng: number } | null>(null);
    const isInitializingRef = useRef<boolean>(false);
    const centerLabelTimerRef = useRef<number | null>(null);

    const extractFeatureLabel = (properties?: Record<string, unknown>): string => {
        const props = properties ?? {};
        const labelCandidates = [
            props["name_zh-Hans"],
            props["name_zh_Hans"],
            props.name_zh,
            props["name:zh"],
            props.name,
            props.name_en,
        ];

        const label = labelCandidates.find((item) => {
            return typeof item === "string" && item.trim().length > 0;
        });

        return typeof label === "string" ? label.trim() : "";
    };

    const getCenterLabelFromMap = (map: any): string => {
        if (!map?.queryRenderedFeatures) {
            return "";
        }

        const container = map.getContainer?.();
        if (!container) {
            return "";
        }

        const centerPixel = {
            x: container.clientWidth / 2,
            y: container.clientHeight / 2,
        };
        const searchRadiusPx = 120;
        const features = map.queryRenderedFeatures(
            [
                [centerPixel.x - searchRadiusPx, centerPixel.y - searchRadiusPx],
                [centerPixel.x + searchRadiusPx, centerPixel.y + searchRadiusPx],
            ],
            {
                layers: undefined,
            }
        ) as Array<{
            layer?: { type?: string };
            properties?: Record<string, unknown>;
            geometry?: { type?: string; coordinates?: any };
        }>;

        if (!features?.length) {
            return "";
        }

        let nearestLabel = "";
        let nearestDistanceSq = Number.POSITIVE_INFINITY;

        for (const feature of features) {
            if (feature?.layer?.type !== "symbol") {
                continue;
            }

            const label = extractFeatureLabel(feature.properties);
            if (!label) {
                continue;
            }

            let distanceSq = Number.POSITIVE_INFINITY;
            const geometryType = feature.geometry?.type;
            const coordinates = feature.geometry?.coordinates;

            if (geometryType === "Point" && Array.isArray(coordinates) && coordinates.length >= 2) {
                try {
                    const projected = map.project({
                        lng: Number(coordinates[0]),
                        lat: Number(coordinates[1]),
                    });
                    const dx = projected.x - centerPixel.x;
                    const dy = projected.y - centerPixel.y;
                    distanceSq = dx * dx + dy * dy;
                } catch (error) {
                    distanceSq = Number.POSITIVE_INFINITY;
                }
            }

            if (distanceSq < nearestDistanceSq) {
                nearestDistanceSq = distanceSq;
                nearestLabel = label;
            }
        }

        return nearestLabel;
    };

    const emitCenterLabel = (map: any, immediate = false) => {
        if (!onCenterLabelChange) {
            return;
        }

        if (centerLabelTimerRef.current) {
            window.clearTimeout(centerLabelTimerRef.current);
            centerLabelTimerRef.current = null;
        }

        const publish = () => {
            onCenterLabelChange(getCenterLabelFromMap(map));
        };

        if (immediate) {
            publish();
            return;
        }

        centerLabelTimerRef.current = window.setTimeout(() => {
            centerLabelTimerRef.current = null;
            publish();
        }, 120);
    };

    useEffect(() => {
        // 只有在坐标有效时才初始化地图
        if (latitude == null || longitude == null || latitude === 0 || longitude === 0) {
            return;
        }

        // 如果地图已经初始化，跳过
        if (mapRef.current) {
            return;
        }

        // 防止重复初始化
        if (isInitializingRef.current) {
            return;
        }

        if (!mapContainerRef.current) {
            return;
        }

        if (!window.mapboxgl) {
            // 加载Mapbox脚本
            isInitializingRef.current = true;
            const script = document.createElement('script');
            script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
            script.onload = () => {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
                document.head.appendChild(link);
                isInitializingRef.current = false;
                initMap();
            };
            document.head.appendChild(script);
        } else {
            isInitializingRef.current = false;
            initMap();
        }

        return () => {
            // 清理地图实例
            if (mapRef.current) {
                try {
                    // 移除所有标记
                    if (markerRef.current) {
                        markerRef.current.remove();
                        markerRef.current = null;
                    }
                    if (originalMarkerRef.current) {
                        originalMarkerRef.current.remove();
                        originalMarkerRef.current = null;
                    }
                    if (currentLocationMarkerRef.current) {
                        currentLocationMarkerRef.current.remove();
                        currentLocationMarkerRef.current = null;
                    }
                    if (currentLocationWatchIdRef.current !== null && navigator.geolocation) {
                        navigator.geolocation.clearWatch(currentLocationWatchIdRef.current);
                        currentLocationWatchIdRef.current = null;
                    }
                    if (centerLabelTimerRef.current) {
                        window.clearTimeout(centerLabelTimerRef.current);
                        centerLabelTimerRef.current = null;
                    }
                    // 移除事件监听器
                    if (mapRef.current) {
                        mapRef.current.off('movestart');
                        mapRef.current.off('move');
                        if (moveendHandlerRef.current) {
                            mapRef.current.off('moveend', moveendHandlerRef.current);
                            moveendHandlerRef.current = null;
                        }
                    }
                    // 移除地图
                    if (mapRef.current.remove) {
                        mapRef.current.remove();
                    }
                } catch (error) {
                    console.error('Error cleaning up map:', error);
                }
                mapRef.current = null;
            }
            setIsMapLoaded(false);
        };
    }, []); // 只在组件挂载时初始化一次

    // 同步外部传入的坐标到内部状态（仅在非拖动选择模式下更新，避免拖动时的循环更新）
    useEffect(() => {
        // 在拖动选择模式下，不响应外部坐标变化，避免干扰用户拖动操作
        if (enableDragToSelect) {
            return;
        }
        
        if (latitude != null && longitude != null && latitude !== 0 && longitude !== 0) {
            // 只有当坐标有明显变化时才更新（避免拖动时的微小变化导致循环）
            const latDiff = Math.abs(latitude - currentLatitude);
            const lngDiff = Math.abs(longitude - currentLongitude);
            // 如果差异大于阈值，说明是外部传入的新坐标，需要更新
            if (latDiff > 0.0001 || lngDiff > 0.0001) {
                setCurrentLatitude(latitude);
                setCurrentLongitude(longitude);
                // 如果地图已加载，更新地图中心
                if (mapRef.current && isMapLoaded) {
                    mapRef.current.setCenter([longitude, latitude]);
                    if (markerRef.current) {
                        markerRef.current.setLngLat([longitude, latitude]);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latitude, longitude, isMapLoaded, enableDragToSelect]);

    const initMap = () => {
        if (!mapContainerRef.current || !window.mapboxgl) return;

        window.mapboxgl.accessToken = DEFAULT_MAPBOX_API_KEY;

        // 使用有效坐标，如果无效则使用默认坐标（纽约）
        const validLat = (latitude != null && latitude !== 0) ? latitude : 40.7128;
        const validLng = (longitude != null && longitude !== 0) ? longitude : -74.0060;

        const map = new window.mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [validLng, validLat],
            zoom: zoom,
            attributionControl: false // 隐藏版权信息控件
        });

        map.on('load', () => {
            setIsMapLoaded(true);
            
            // 添加导航控制（放大缩小按钮）
            map.addControl(new window.mapboxgl.NavigationControl(), 'bottom-right');
            
            // 添加原始位置标记（始终显示，方便对比移动距离）- 使用与订单详情页相同的黑色大头针样式
            if (originalLatitude != null && originalLongitude != null) {
                // 创建自定义的黑色大头针标记，参照订单详情页样式
                const markerElement = document.createElement('div');
                markerElement.innerHTML = `
                    <div class="${styles['custom-map-pin']}">
                        <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 0C8 0 0 7 0 16C0 28 16 40 16 40C16 40 32 28 32 16C32 7 24 0 16 0Z" fill="black"/>
                        </svg>
                        <span class="${styles['custom-map-pin-number']}">
                            ${stopNumber || '原'}
                        </span>
                    </div>
                `;
                
                originalMarkerRef.current = new window.mapboxgl.Marker({
                    element: markerElement,
                    anchor: 'bottom'
                })
                    .setLngLat([originalLongitude, originalLatitude])
                    .addTo(map);
            }

            if (enableDragToSelect) {
                emitCenterLabel(map, true);
                // 拖动选择模式：不添加跟随地图的 Marker，使用固定在屏幕中心的 CSS 指示器
                // 监听地图拖动开始事件，隐藏提示文案
                const movestartHandler = () => {
                    setIsDragging(true);
                };
                
                // 监听地图移动事件，实时更新显示的坐标
                const moveHandler = () => {
                    if (!mapRef.current) return;
                    const center = mapRef.current.getCenter();
                    const lat = center.lat;
                    const lng = center.lng;
                    // 实时更新显示的坐标
                    setCurrentLatitude(lat);
                    setCurrentLongitude(lng);
                    // 实时调用回调通知父组件，使下方坐标实时更新
                    if (onCoordinateChange) {
                        onCoordinateChange(lat, lng);
                    }
                    emitCenterLabel(map);
                };
                
                // 监听地图移动结束事件，在拖动停止后调用回调更新坐标
                const moveendHandler = () => {
                    if (!mapRef.current) return;
                    const center = mapRef.current.getCenter();
                    const lat = center.lat;
                    const lng = center.lng;
                    // 确保坐标已更新
                    setCurrentLatitude(lat);
                    setCurrentLongitude(lng);
                    // 拖动停止后，调用回调通知父组件
                    if (onCoordinateChange) {
                        onCoordinateChange(lat, lng);
                    }
                    emitCenterLabel(map, true);
                    // 拖动结束后，延迟显示提示文案
                    setTimeout(() => {
                        setIsDragging(false);
                    }, 500);
                };
                
                moveendHandlerRef.current = moveendHandler;
                map.on('movestart', movestartHandler);
                map.on('move', moveHandler);
                map.on('moveend', moveendHandler);
            } else {
                // 普通模式：添加当前选中位置标记，设置为可拖拽
                if (validLat != null && validLng != null && validLat !== 0 && validLng !== 0) {
                    markerRef.current = new window.mapboxgl.Marker({ color: '#1890ff', draggable: true })
                        .setLngLat([validLng, validLat])
                        .addTo(map);

                    // 监听标记点拖拽事件
                    markerRef.current.on('dragend', () => {
                        if (!markerRef.current) return;
                        const lngLat = markerRef.current.getLngLat();
                        setCurrentLatitude(lngLat.lat);
                        setCurrentLongitude(lngLat.lng);
                        if (onCoordinateChange) {
                            onCoordinateChange(lngLat.lat, lngLat.lng);
                        }
                    });
                }

                // 监听地图点击事件，允许点击地图来更新位置
                map.on('click', (e: any) => {
                    const { lng, lat } = e.lngLat;
                    setCurrentLatitude(lat);
                    setCurrentLongitude(lng);
                    if (markerRef.current) {
                        markerRef.current.setLngLat([lng, lat]);
                    }
                    if (onCoordinateChange) {
                        onCoordinateChange(lat, lng);
                    }
                });
            }
        });

        mapRef.current = map;
    };

    // 初始化并监听司机当前位置，绘制蓝点标记（进入页面即尝试获取）
    useEffect(() => {
        if (!isMapLoaded || !mapRef.current) {
            return;
        }

        let cancelled = false;

        const upsertCurrentLocationMarker = (lat: number, lng: number) => {
            if (!mapRef.current) return;
            // 创建或更新蓝点
            if (!currentLocationMarkerRef.current) {
                const el = document.createElement('img');
                el.src = DriverLocationSvg;
                el.style.width = '90px';
                el.style.height = '90px';
                el.style.display = 'block';
                currentLocationMarkerRef.current = new window.mapboxgl.Marker({
                    element: el,
                    anchor: 'center',
                    offset: [0, 10] // 初始偏移，保证圆心落在坐标点
                });
            }
            currentLocationMarkerRef.current.setLngLat([lng, lat]).addTo(mapRef.current);
        };

        const startWatch = async () => {
            // 尝试获取一次当前位置（不强制居中，只显示标记）
            // 注意：这里不显示错误提示，因为watchPosition会继续尝试获取位置
            try {
                const pos = await geolocationService.getOneTimePosition();
                if (!cancelled) {
                    setDriverLocation({ lat: pos.latitude, lng: pos.longitude });
                    upsertCurrentLocationMarker(pos.latitude, pos.longitude);
                }
            } catch (error) {
                // 初始化定位失败不显示错误，因为watchPosition会继续监听
                // 只有在后续操作（如点击回定位按钮）失败时才提示用户
                console.warn('Init current location failed, will retry with watchPosition:', error);
            }

            if (!navigator.geolocation) {
                return;
            }

            // 开始监听位置变化，实时更新蓝点
            currentLocationWatchIdRef.current = navigator.geolocation.watchPosition(
                (position) => {
                    if (cancelled) return;
                    const { latitude: lat, longitude: lng } = position.coords;
                    setDriverLocation({ lat, lng });
                    upsertCurrentLocationMarker(lat, lng);
                },
                (error) => {
                    if (!locationErrorShownRef.current) {
                        // 静默处理，避免在页面初始化时弹出“未选择”干扰用户
                        console.warn('Watch position failed:', error);
                        locationErrorShownRef.current = true;
                    }
                    if (currentLocationWatchIdRef.current !== null && navigator.geolocation) {
                        navigator.geolocation.clearWatch(currentLocationWatchIdRef.current);
                        currentLocationWatchIdRef.current = null;
                    }
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 10000,
                    timeout: 10000
                }
            );
        };

        startWatch();

        return () => {
            cancelled = true;
            if (currentLocationWatchIdRef.current !== null && navigator.geolocation) {
                navigator.geolocation.clearWatch(currentLocationWatchIdRef.current);
                currentLocationWatchIdRef.current = null;
            }
            if (currentLocationMarkerRef.current) {
                currentLocationMarkerRef.current.remove();
                currentLocationMarkerRef.current = null;
            }
        };
    }, [isMapLoaded]);

    const handleCenterToLocation = () => {
        if (mapRef.current && latitude != null && longitude != null && latitude !== 0 && longitude !== 0) {
            mapRef.current.flyTo({
                center: [longitude, latitude],
                zoom: 15
            });
        }
    };

    const handleReturnToOriginal = async () => {
        let targetLat: number | null = driverLocation?.lat ?? null;
        let targetLng: number | null = driverLocation?.lng ?? null;

        // 如果当前有缓存的司机位置就直接用，否则尝试获取最新位置
        if (targetLat == null || targetLng == null) {
            try {
                const position = await geolocationService.getFreshPosition();
                targetLat = position.latitude;
                targetLng = position.longitude;
                setDriverLocation({ lat: targetLat, lng: targetLng });
            } catch (error) {
                console.warn('Failed to get current driver location, fallback to original coordinates:', error);
            }
        }

        // 如果依然没有，则回退到原始地址坐标
        if (targetLat == null || targetLng == null) {
            if (mapRef.current && originalLatitude != null && originalLongitude != null) {
                mapRef.current.flyTo({
                    center: [originalLongitude, originalLatitude],
                    zoom: 15
                });
                setCurrentLatitude(originalLatitude);
                setCurrentLongitude(originalLongitude);
                if (onCoordinateChange) {
                    onCoordinateChange(originalLatitude, originalLongitude);
                }
            }
            return;
        }

        if (mapRef.current) {
            mapRef.current.flyTo({
                center: [targetLng, targetLat],
                zoom: 15
            });
            // 绘制/更新司机当前位置的蓝点标记
            const el = document.createElement('img');
            el.src = DriverLocationSvg;
            el.style.width = '90px';
            el.style.height = '90px';
            el.style.display = 'block';
            if (currentLocationMarkerRef.current) {
                currentLocationMarkerRef.current.remove();
            }
            currentLocationMarkerRef.current = new window.mapboxgl.Marker({
                element: el,
                anchor: 'center',
                offset: [0, 10] // 圆心对准坐标点
            }).setLngLat([targetLng, targetLat]).addTo(mapRef.current);
        }
        setCurrentLatitude(targetLat);
        setCurrentLongitude(targetLng);
        if (onCoordinateChange) {
            onCoordinateChange(targetLat, targetLng);
        }
    };

    return (
        <div className="relative h-full w-full [&_.mapboxgl-ctrl-bottom-right]:bottom-[70px] [&_.mapboxgl-ctrl-bottom-right]:!right-0 [&_.mapboxgl-ctrl-right_.mapboxgl-ctrl]:!m-0">
            <div
                ref={mapContainerRef}
                className="w-full h-full"
            />
            {/* 中心点指示器（拖动选择模式）- 灰色大头针样式 */}
            {showCenterIndicator && enableDragToSelect && (
                <div 
                    className="absolute top-1/2 left-1/2 pointer-events-none z-[9999] -translate-x-1/2 -translate-y-[71%]"
                >
                    {/* 提示文案（悬浮在大头针上方，拖动时隐藏） */}
                    {!isDragging && (
                        <div 
                            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[10000] w-fit min-w-[42vw] max-w-[80vw]"
                        >
                            <div className="bg-white text-gray-600 text-sm px-3 py-1.5 rounded-lg shadow-lg border border-gray-200 break-words text-center">
                                {t('pages.delivery.addressCoordinateFeedback.dragToSelect')}
                            </div>
                            {/* 小三角形指向大头针 */}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-0.5">
                                <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-white"></div>
                            </div>
                        </div>
                    )}
                    {/* 灰色圆圈白色空心下方针的图标 */}
                    <div 
                        className="flex flex-col items-center justify-center w-8 h-10"
                    >
                        <PinGrayIcon 
                            width={32} 
                            height={45} 
                            className="drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
                        />
                    </div>
                </div>
            )}
            {/* 按钮区域 */}
            {!enableDragToSelect && (
                <div className="absolute bottom-4 left-4 right-4 flex gap-2 z-10">
                    <Button
                        size="small"
                        color="primary"
                        onClick={handleCenterToLocation}
                    >
                        {t('pages.delivery.addressCoordinateFeedback.moveToLocation')}
                    </Button>
                    {onLocationSelect && (
                        <Button
                            size="small"
                            color="primary"
                            onClick={onLocationSelect}
                        >
                            {t('pages.delivery.addressCoordinateFeedback.selectPosition')}
                        </Button>
                    )}
                </div>
            )}
            {/* 回定位按钮（右下角，在放大缩小按钮下方，垂直对齐） */}
            {originalLatitude != null && originalLongitude != null && (
                <button
                    onClick={handleReturnToOriginal}
                    className="absolute bottom-4 right-2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-transparent border-none p-0 cursor-pointer"
                >
                    <LocationTargetIcon 
                        width={40} 
                        height={40} 
                        className="flex-shrink-0"
                    />
                </button>
            )}
        </div>
    );
};

export default AddressCoordinateFeedbackMapView;

